import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { Link, Navigate } from "react-router-dom";
import { toast } from "react-toastify";
import {
  getSecurityOverview,
  listSecurityAdvisories,
  listSecurityAffectedEdges,
  createSecurityCategoryRollout,
  notifySecurityRollout,
  getSecurityRolloutPreview,
  listOtaReleases,
  scanSecurityReleases,
} from "../api/otaAdminApi";
import SecurityExposure from "../components/SecurityExposure";
import { formatDate } from "../utils/i18nFormat";

const CATEGORIES = ["kev", "critical", "high", "medium", "low"];

const severity = {
  critical: { color: "#f87171", soft: "rgba(248,113,113,.13)" },
  high: { color: "#fb923c", soft: "rgba(251,146,60,.13)" },
  medium: { color: "#facc15", soft: "rgba(250,204,21,.13)" },
  low: { color: "#67e8f9", soft: "rgba(103,232,249,.13)" },
};

const scanDetail = (t, o) => (o.last_scan_status
  ? t("admin.security.scanDetail", { os: o.last_scan_os_version || "?", status: t(`admin.security.scanStatus.${o.last_scan_status}`, { defaultValue: o.last_scan_status }) })
  : t("admin.security.noScanYet"));

function Severity({ value }) {
  const { t } = useTranslation();
  const key = severity[String(value || "medium").toLowerCase()] ? String(value || "medium").toLowerCase() : "medium";
  const item = severity[key];
  return <span style={{ ...styles.badge, color: item.color, background: item.soft }}>{t(`admin.security.categories.${key}`)}</span>;
}

function Stat({ eyebrow, value, detail, tone = "cyan" }) {
  return (
    <div style={{ ...styles.stat, "--tone": tone === "red" ? "#fb7185" : tone === "amber" ? "#fbbf24" : "#67e8f9" }}>
      <div style={styles.statLine} />
      <div style={styles.eyebrow}>{eyebrow}</div>
      <div style={styles.statValue}>{value ?? "—"}</div>
      <div style={styles.statDetail}>{detail}</div>
    </div>
  );
}

export default function SecurityCenter() {
  const { t } = useTranslation();
  const accessToken = useSelector((s) => s.session?.accessToken);
  const me = useSelector((s) => s.session?.me);
  const [overview, setOverview] = useState(null);
  const [advisories, setAdvisories] = useState([]);
  const [selected, setSelected] = useState(null);
  const [affectedEdges, setAffectedEdges] = useState([]);
  const [busy, setBusy] = useState(false);
  const [cats, setCats] = useState(() => ["kev", "critical"]);
  const [preview, setPreview] = useState(null);
  const [releases, setReleases] = useState([]);
  const [releaseId, setReleaseId] = useState("");
  const [percent, setPercent] = useState(1);

  const refresh = useCallback(async () => {
    if (!accessToken) return;
    const [summary, list] = await Promise.all([
      getSecurityOverview(accessToken),
      listSecurityAdvisories(accessToken, { limit: 50, categories: cats.length ? cats.join(",") : undefined }),
    ]);
    setOverview(summary);
    setAdvisories(list.items || list || []);
  }, [accessToken, cats]);

  useEffect(() => {
    refresh().catch(() => toast.error(t("admin.security.loadFailed")));
  }, [refresh, t]);

  const openAdvisory = async (item) => {
    setSelected(item);
    try {
      setAffectedEdges(await listSecurityAffectedEdges(accessToken, item.id));
    } catch {
      toast.error(t("admin.security.affectedLoadFailed"));
    }
  };

  const toggleCat = (name) => setCats((prev) => (prev.includes(name) ? prev.filter((c) => c !== name) : [...prev, name]));

  // what the chosen categories would cover, and the releases that can fix them
  useEffect(() => {
    if (!accessToken || cats.length === 0) {
      setPreview(null);
      return;
    }
    getSecurityRolloutPreview(accessToken, cats).then(setPreview).catch(() => setPreview(null));
  }, [accessToken, cats, overview?.by_category]);

  useEffect(() => {
    if (!accessToken) return;
    listOtaReleases(accessToken, { limit: 50 })
      .then((res) => setReleases((res.items || []).filter((r) => r.status === "published" && ["valonix-os-ota", "valonix-hotfix"].includes(r.artifact_type))))
      .catch(() => setReleases([]));
  }, [accessToken]);

  const startRollout = async () => {
    const release = releases.find((r) => r.id === releaseId);
    const names = CATEGORIES.filter((k) => cats.includes(k)).map((k) => t(`admin.security.categories.${k}`)).join(", ");
    if (!release || !preview) return;
    if (!window.confirm(t("admin.security.rolloutConfirm", { version: release.version, names, advisories: preview.advisories, edges: preview.affected_edges, percent }))) return;
    setBusy(true);
    try {
      const rollout = await createSecurityCategoryRollout(accessToken, { categories: cats, release_id: releaseId, rollout_percentage: Number(percent) });
      toast.success(t("admin.security.rolloutStarted"));
      if (window.confirm(t("admin.security.notifyConfirm"))) {
        try {
          const result = await notifySecurityRollout(accessToken, rollout.id);
          toast.success(t("admin.security.notifyQueued", { count: result.recipients }));
        } catch (error) {
          toast.error(error.response?.data?.detail || t("admin.security.notifyFailed"));
        }
      }
      await refresh();
    } catch (error) {
      toast.error(error.response?.data?.detail || t("admin.security.rolloutFailed"));
    } finally {
      setBusy(false);
    }
  };

  const scanNow = async () => {
    setBusy(true);
    try {
      await scanSecurityReleases(accessToken);
      toast.info(t("admin.security.scanStarted"));
      await refresh();
    } catch (error) {
      toast.error(error.response?.data?.detail || t("admin.security.scanFailed"));
    } finally {
      setBusy(false);
    }
  };

  // while a scan runs, refresh the numbers every 15 s so the result shows up on its own
  useEffect(() => {
    if (!overview?.running_scans) return undefined;
    const timer = setInterval(() => refresh().catch(() => {}), 15000);
    return () => clearInterval(timer);
  }, [overview?.running_scans, refresh]);

  const counts = useMemo(() => overview || {}, [overview]);
  if (me && me.global_role !== "superadmin") return <Navigate to="/home" replace />;

  return (
    <div style={styles.page}>
      <style>{css}</style>
      <header style={styles.hero}>
        <div>
          <div style={styles.kicker}><span style={styles.liveDot} /> SECURITY CONTROL PLANE</div>
          <h1 style={styles.title}>{t("admin.security.title")}</h1>
          <p style={styles.subtitle}>{t("admin.security.subtitle")}</p>
        </div>
        <div style={styles.heroMark}>VX<span>·</span>SEC</div>
      </header>

      <section style={styles.statsGrid}>
        <Stat eyebrow={t("admin.security.stat.openFindings")} value={counts.open_findings} detail={t("admin.security.stat.openFindingsDetail")} />
        <Stat eyebrow={t("admin.security.stat.critical")} value={counts.critical_findings} detail={t("admin.security.stat.criticalDetail")} tone="red" />
        <Stat eyebrow={t("admin.security.stat.affected")} value={counts.affected_edges} detail={t("admin.security.stat.affectedDetail")} tone="amber" />
        <Stat eyebrow={t("admin.security.stat.lastScan")} value={counts.last_scan_at ? formatDate(counts.last_scan_at, { dateStyle: "short", timeStyle: "short" }) : "—"} detail={scanDetail(t, counts)} />
      </section>

      <div style={styles.scanBar}>
        <div>
          <div style={styles.eyebrow}>{t("admin.security.auto.eyebrow")}</div>
          <strong style={{ display: "block", marginTop: 5 }}>
            {t("admin.security.auto.text")}
            {counts.running_scans ? t("admin.security.auto.running", { count: counts.running_scans }) : ""}
          </strong>
        </div>
        <button disabled={busy || !!counts.running_scans} type="button" onClick={scanNow} style={styles.primary}>
          {t("admin.security.auto.scanNow")}
        </button>
      </div>

      <section style={styles.scanBar} aria-label={t("admin.security.ota.aria")}>
        <div style={{ flex: "1 1 100%" }}>
          <div style={styles.eyebrow}>{t("admin.security.ota.eyebrow")}</div>
          <strong style={{ display: "block", margin: "5px 0 10px" }}>{t("admin.security.ota.question")}</strong>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            {CATEGORIES.map((key) => (
              <label key={key} style={{ ...styles.chip, ...(cats.includes(key) ? styles.chipOn : {}) }}>
                <input type="checkbox" checked={cats.includes(key)} onChange={() => toggleCat(key)} style={{ display: "none" }} />
                {t(`admin.security.categories.${key}`)} · {counts.by_category?.[key] ?? 0}
              </label>
            ))}
          </div>
        </div>
        <div style={{ flex: "1 1 260px", color: "#9fb1c7", fontSize: 13 }}>
          {preview
            ? t("admin.security.ota.preview", { advisories: preview.advisories, edges: preview.affected_edges, versions: preview.os_versions.join(", ") || "—" })
            : t("admin.security.ota.pickCategory")}
        </div>
        <select value={releaseId} onChange={(e) => setReleaseId(e.target.value)} style={styles.versionInput} aria-label={t("admin.security.ota.fixRelease")}>
          <option value="">{t("admin.security.ota.pickRelease")}</option>
          {releases.map((r) => <option key={r.id} value={r.id}>{r.version}{r.artifact_type === "valonix-hotfix" ? t("admin.security.ota.stackHotfix") : " · OS"}</option>)}
        </select>
        <input type="number" min="0" max="100" value={percent} onChange={(e) => setPercent(e.target.value)} style={{ ...styles.versionInput, width: 70 }} aria-label={t("admin.security.ota.canaryPercent")} />
        <button type="button" disabled={busy || !releaseId || !preview || preview.advisories === 0} onClick={startRollout} style={styles.primary}>
          {t("admin.security.ota.start")}
        </button>
      </section>

      <SecurityExposure accessToken={accessToken} />

      <div style={styles.layout}>
        <section style={styles.panel}>
          <div style={styles.panelHeader}>
            <div><div style={styles.eyebrow}>{t("admin.security.threat.eyebrow")}</div><h2 style={styles.panelTitle}>{t("admin.security.threat.title")}</h2></div>
            <span style={styles.count}>{t("admin.security.threat.count", { count: advisories.length })}</span>
          </div>
          {advisories.length === 0 ? (
            <div style={styles.empty}><div style={styles.emptyIcon}>✓</div><strong>{t("admin.security.threat.emptyTitle")}</strong><span>{t("admin.security.threat.emptyText")}</span></div>
          ) : (
            <div style={styles.findings}>
              {advisories.map((item) => (
                <button key={item.id} type="button" onClick={() => openAdvisory(item)} style={{ ...styles.finding, ...(selected?.id === item.id ? styles.findingActive : {}) }}>
                  <div style={styles.findingTop}><code>{item.cve_id}</code><Severity value={item.severity} /></div>
                  <strong>{item.title || item.summary || t("admin.security.threat.defaultTitle")}</strong>
                  <div style={styles.findingMeta}>{item.scope === "stack" ? "Stack" : item.scope === "both" ? "OS+Stack" : "OS"} · {item.component_name || t("admin.security.threat.noComponent")} · {t("admin.security.threat.devices", { count: item.affected_edges ?? 0 })} · {item.first_seen_at ? formatDate(item.first_seen_at, { dateStyle: "short" }) : t("admin.security.threat.isNew")}</div>
                </button>
              ))}
            </div>
          )}
        </section>

        <aside style={styles.detailPanel}>
          {selected ? (
            <>
              <div style={styles.panelHeader}><div><div style={styles.eyebrow}>{t("admin.security.detail.eyebrow")}</div><h2 style={styles.panelTitle}>{selected.cve_id}</h2></div><Severity value={selected.severity} /></div>
              <p style={styles.detailText}>{selected.summary || selected.title || t("admin.security.detail.noDescription")}</p>
              <div style={styles.detailGrid}>
                <div><span>{t("admin.security.detail.component")}</span><strong>{selected.component_name || "—"}</strong></div>
                <div><span>{t("admin.security.detail.source")}</span><strong>{selected.source || "cra-checker"}</strong></div>
                <div><span>{t("admin.security.detail.kev")}</span><strong>{selected.is_kev ? t("admin.security.detail.yes") : t("admin.security.detail.no")}</strong></div>
                <div><span>{t("admin.security.detail.deviceCount")}</span><strong>{affectedEdges.length}</strong></div>
              </div>
              <div style={styles.deviceList}>
                <div style={styles.eyebrow}>{t("admin.security.detail.devicesEyebrow")}</div>
                {affectedEdges.length === 0 ? <span style={styles.muted}>{t("admin.security.detail.noInventory")}</span> : affectedEdges.slice(0, 8).map((edge) => <Link key={edge.id} to={`/edges/${edge.id}`} style={styles.device}><span style={styles.deviceDot} />{edge.name || edge.edge_id}<span>{edge.reported_os_version || "—"}</span></Link>)}
              </div>
            </>
          ) : (
            <div style={styles.selectHint}><div style={styles.selectOrb}>⌁</div><strong>{t("admin.security.detail.selectTitle")}</strong><span>{t("admin.security.detail.selectText")}</span></div>
          )}
        </aside>
      </div>

      <div style={styles.footerNote}><span>●</span> {t("admin.security.footer")}</div>
    </div>
  );
}

const styles = {
  page: { minHeight: "100%", padding: "8px 4px 34px", color: "#e5eef8" },
  hero: { display: "flex", justifyContent: "space-between", alignItems: "end", padding: "22px 6px 28px", borderBottom: "1px solid rgba(148,163,184,.18)" },
  kicker: { color: "#67e8f9", fontSize: 11, fontWeight: 800, letterSpacing: ".18em" },
  liveDot: { display: "inline-block", width: 7, height: 7, borderRadius: "50%", background: "#67e8f9", boxShadow: "0 0 14px #67e8f9", marginRight: 9 },
  title: { margin: "10px 0 5px", fontSize: 34, letterSpacing: "-.04em", color: "#f8fafc" },
  subtitle: { maxWidth: 670, margin: 0, color: "#9fb1c7", lineHeight: 1.6 },
  heroMark: { fontSize: 28, fontWeight: 900, letterSpacing: ".12em", color: "#334155" },
  statsGrid: { display: "grid", gridTemplateColumns: "repeat(4, minmax(0, 1fr))", gap: 12, margin: "18px 0" },
  stat: { position: "relative", overflow: "hidden", padding: "17px 18px", border: "1px solid rgba(148,163,184,.17)", borderRadius: 14, background: "linear-gradient(145deg, rgba(15,36,59,.95), rgba(9,22,38,.9))", boxShadow: "0 12px 28px rgba(2,8,23,.12)" },
  statLine: { position: "absolute", top: 0, left: 0, right: 0, height: 2, background: "var(--tone)" },
  eyebrow: { color: "#6f8ba8", fontSize: 10, fontWeight: 800, letterSpacing: ".16em" },
  statValue: { marginTop: 10, fontSize: 29, fontWeight: 800, color: "#f8fafc" },
  statDetail: { marginTop: 4, color: "#91a5bb", fontSize: 12 },
  layout: { display: "grid", gridTemplateColumns: "minmax(0, 1.15fr) minmax(350px, .85fr)", gap: 14 },
  panel: { minHeight: 440, border: "1px solid rgba(148,163,184,.17)", borderRadius: 16, background: "#0b1b2d", padding: 20 },
  detailPanel: { minHeight: 440, border: "1px solid rgba(103,232,249,.22)", borderRadius: 16, background: "radial-gradient(circle at 100% 0, rgba(20,91,112,.2), transparent 42%), #0b1b2d", padding: 20 },
  panelHeader: { display: "flex", justifyContent: "space-between", alignItems: "start", gap: 12, marginBottom: 18 },
  panelTitle: { margin: "7px 0 0", color: "#f8fafc", fontSize: 20 },
  count: { color: "#8da2b9", fontSize: 12, padding: "5px 9px", border: "1px solid rgba(148,163,184,.2)", borderRadius: 99 },
  empty: { minHeight: 330, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 10, color: "#b9c8d8", textAlign: "center" },
  emptyIcon: { display: "grid", placeItems: "center", width: 48, height: 48, borderRadius: "50%", color: "#67e8f9", border: "1px solid rgba(103,232,249,.45)", fontSize: 23 },
  findings: { display: "grid", gap: 8 },
  finding: { display: "grid", gap: 7, textAlign: "left", color: "#dbe7f3", padding: "14px 15px", border: "1px solid rgba(148,163,184,.13)", borderRadius: 11, background: "rgba(15,38,61,.55)", cursor: "pointer" },
  findingActive: { borderColor: "rgba(103,232,249,.7)", background: "rgba(14,67,87,.42)" },
  findingTop: { display: "flex", justifyContent: "space-between", alignItems: "center" },
  findingMeta: { color: "#7f98b2", fontSize: 12 },
  badge: { padding: "4px 8px", borderRadius: 99, fontSize: 10, fontWeight: 800 },
  detailText: { color: "#aabbd0", lineHeight: 1.6, fontSize: 13 },
  detailGrid: { display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, margin: "20px 0", padding: "14px 0", borderTop: "1px solid rgba(148,163,184,.15)", borderBottom: "1px solid rgba(148,163,184,.15)" },
  actionBox: { display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12, padding: 14, borderRadius: 12, background: "rgba(103,232,249,.07)", border: "1px solid rgba(103,232,249,.2)" },
  primary: { border: 0, borderRadius: 8, padding: "10px 13px", background: "#67e8f9", color: "#062433", fontWeight: 800, cursor: "pointer", whiteSpace: "nowrap" },
  deviceList: { display: "grid", gap: 8, marginTop: 22 },
  deviceDot: { width: 6, height: 6, borderRadius: "50%", background: "#fbbf24" },
  device: { display: "grid", gridTemplateColumns: "8px 1fr auto", alignItems: "center", gap: 8, color: "#dbe7f3", textDecoration: "none", fontSize: 13 },
  muted: { color: "#7f98b2", fontSize: 13 },
  selectHint: { minHeight: 390, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 10, textAlign: "center", color: "#aabbd0" },
  selectOrb: { display: "grid", placeItems: "center", width: 60, height: 60, borderRadius: 18, color: "#67e8f9", background: "rgba(103,232,249,.08)", border: "1px solid rgba(103,232,249,.28)", fontSize: 30 },
  footerNote: { marginTop: 14, color: "#6f8ba8", fontSize: 12 },
  scanBar: { display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap", padding: "14px 16px", marginBottom: 14, border: "1px solid rgba(103,232,249,.18)", borderRadius: 14, background: "rgba(9,31,49,.8)" },
  chip: { padding: "7px 12px", borderRadius: 99, border: "1px solid rgba(148,163,184,.3)", color: "#9fb1c7", cursor: "pointer", fontSize: 13 },
  chipOn: { borderColor: "#67e8f9", background: "rgba(103,232,249,.12)", color: "#e5eef8", fontWeight: 700 },
  fileInput: { flex: "1 1 220px", color: "#9fb1c7", fontSize: 12 },
  versionInput: { width: 150, padding: "10px 11px", borderRadius: 8, border: "1px solid rgba(148,163,184,.25)", background: "#071525", color: "#e5eef8" },
};

const css = `
  @keyframes securityPulse { 0%,100% { opacity:.65 } 50% { opacity:1 } }
  code { color:#a5f3fc; font-family: ui-monospace, SFMono-Regular, Menlo, monospace; font-size:12px }
  @media (max-width: 900px) { .security-page-grid { grid-template-columns:1fr } }
`;
